// Utility to composite a rendered canvas onto an opaque background color
// The fill color is determined by sampling the canvas; if transparent, fall back to the viewer's background color

export function getViewerBackgroundColor(instance?: any): string {
  try {
    const viewerEl = instance?.Core?.documentViewer?.getScrollViewElement?.();
    const computed = viewerEl ? window.getComputedStyle(viewerEl).backgroundColor : '';
    return computed || '#ffffff';
  } catch (_) {
    return '#ffffff';
  }
}

export function deriveCanvasBackgroundColor(canvas: HTMLCanvasElement, instance?: any): string {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return getViewerBackgroundColor(instance);
    const sample = ctx.getImageData(0, 0, 1, 1).data;
    if (sample[3] === 0) {
      return getViewerBackgroundColor(instance);
    }
    return `rgba(${sample[0]}, ${sample[1]}, ${sample[2]}, 1)`;
  } catch (_) {
    return getViewerBackgroundColor(instance);
  }
}

export function compositeCanvasToOpaquePNG(canvas: HTMLCanvasElement, instance?: any): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      const composite = document.createElement('canvas');
      composite.width = canvas.width;
      composite.height = canvas.height;
      const ctx = composite.getContext('2d');
      if (ctx) {
        // Draw the original canvas content first
        ctx.drawImage(canvas, 0, 0);

        // Draw a solid white background behind it to cover any transparency
        // (same technique as GraphicZone flashing annotations)
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, composite.width, composite.height);
      }
      composite.toBlob((blob) => resolve(blob), 'image/png');
    } catch (_) {
      resolve(null);
    }
  });
}

export function renderRectToOpaquePNG(
  doc: any,
  pageNumber: number,
  renderRect: any,
  instance?: any,
  zoom: number = 2,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      doc.loadCanvas({
        pageNumber,
        renderRect,
        zoom,
        drawComplete: (canvasOrImage: HTMLCanvasElement | HTMLImageElement) => {
          let canvas: HTMLCanvasElement;

          // Use duck typing instead of instanceof due to cross-realm (iframe) issues
          if ('getContext' in canvasOrImage && typeof canvasOrImage.getContext === 'function') {
            canvas = canvasOrImage as HTMLCanvasElement;
          } else {
            // If it's an image, draw it onto a canvas first
            canvas = document.createElement('canvas');
            canvas.width = canvasOrImage.width;
            canvas.height = canvasOrImage.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              resolve(null);
              return;
            }

            ctx.drawImage(canvasOrImage, 0, 0);
          }

          compositeCanvasToOpaquePNG(canvas, instance)
            .then((blob) => resolve(blob))
            .catch(() => resolve(null));
        },
      });
    } catch (_) {
      resolve(null);
    }
  });
}
